import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter, Link } from "react-router-dom";

import { graphql, withApollo } from "@apollo/client/react/hoc";
import compose from "lodash.flowright";
import styled from "styled-components";

import { GET_GUEST_FREE_BOX_CAMPAIGN_DISCOUNT } from "../../../graphql/queries";
import {
  executeLogInRequest,
  getLocalStorageToken,
  runSubmitQuizMutation,
  isPhysicalStoreVisit,
  getStoreLocationId,
} from "../../../helpers/auth";
import { readAuthToken, writeAuthToken } from "../../../helpers/tokenStorage";
import {
  FETCH_POLICY_CACHE_ONLY,
  ERROR_MESSAGE_INVALID_GRANT,
  REDIRECT_URL_PARAM,
} from "../../../helpers/constants";
import urlPatterns from "../../../urls";
import { logError } from "../../../helpers/logging";
import { fonts } from "../../../styles/variables";
import breakpoints from "../../../styles/breakpoints";
import { showErrorAlert } from "../../../helpers/alerts";
import { getGuestCheckoutLoginRedirect } from "../../../helpers/guestCheckoutLoginRedirect";

import Button from "../../atoms/Button";
import InputField from "../../atoms/InputField";
import GlobalAlert from "../../atoms/GlobalAlert/GlobalAlert";
import { StyledInputHolder } from "../../atoms/Input";

const CheckoutInputHolderPassword = styled(StyledInputHolder)`
  > input {
    padding-right: 120px;
  }
`;

const CheckoutInputHolderPasswordLink = styled(Link)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  text-transform: uppercase;
  line-height: 0.91;
  letter-spacing: 1.1px;
  font-size: 11px;
  font-family: ${fonts.fontInterstateUltraBlack};
  display: inline-block;
  padding: 5px 20px;
`;

const StyledFormBtn = styled(Button)`
  margin-bottom: 20px;
  ${breakpoints.smDown} {
    margin-top: 10px;
  }
`;

class LogInForm extends Component {
  static propTypes = {
    location: PropTypes.shape({
      state: PropTypes.shape({
        redirectUrl: PropTypes.string,
      }),
    }).isRequired,
    guestFreeBoxCampaignDiscountQuery: PropTypes.shape({
      guestFreeBoxCampaignDiscount: PropTypes.shape({
        freeBoxCampaignId: PropTypes.number,
      }),
    }).isRequired,
    history: PropTypes.shape({}).isRequired,
  };

  state = {
    form: {
      email: "",
      password: "",
    },
    auth: {
      accessToken: "",
      refreshToken: "",
      encodedTokens: readAuthToken() || "",
    },
    genericErrorMessage: "",
    isSubmitInProgress: false,
    isSubmitQuizInProgress: false,
  };

  async componentDidMount() {
    const { state } = this;

    // Decodes tokens from localStorage --> https://www.npmjs.com/package/jsonwebtoken
    const jwToken = state.auth.encodedTokens && getLocalStorageToken();

    if (jwToken) {
      this.setState({
        form: {
          email: jwToken.email,
        },
        auth: {
          ...state.auth,
          accessToken: jwToken.accessToken,
          refreshToken: jwToken.refreshToken,
        },
      });
    }
  }

  /**
   * Assigns new values to 'this.state.form' properties
   * @param field
   * @param value
   * */
  handleChange = (field, value) => {
    const { form } = this.state;
    this.setState({ form: { ...form, [field]: value } });
  };

  /**
   * Validates and submits the form.
   *
   * @param event
   * */
  handleSubmit = (event) => {
    event.preventDefault();

    const { state } = this;
    const { ...LogInInput } = state.form;

    this.setState({ genericErrorMessage: "", isSubmitInProgress: true });

    // Verifies all fields were filled out
    const hasEmptyFields = Object.values(LogInInput).includes("");
    if (hasEmptyFields) {
      this.setState({
        genericErrorMessage: "Sorry, all fields are required!",
        isSubmitInProgress: false,
      });
      return false;
    }

    return this.sendLogInMutation();
  };

  /**
   * Executes login and redirects to my account page if login successful
   * */
  sendLogInMutation = async () => {
    const { state, props } = this;
    const { history } = props;
    const { email, password } = state.form;
    const { freeBoxCampaignId } = props.guestFreeBoxCampaignDiscountQuery.guestFreeBoxCampaignDiscount;

    // Removes the persisted token (web + Android localStorage, iOS NSUserDefaults) if already set.
    if (readAuthToken()) {
      writeAuthToken(null);
    }

    const queryParams = new URLSearchParams(window.location.search);
    const redirectUrlFromParams = queryParams.get(REDIRECT_URL_PARAM);

    // Executes LogIn and redirects user to MyAccount page
    executeLogInRequest(email, password)
      .then(async () => {
        const quizData = props.location.state && props.location.state.quizData;
        if (quizData) {
          const storeLocationIdInt = getStoreLocationId();

          this.setState({ isSubmitQuizInProgress: true });
          await runSubmitQuizMutation({
            ...quizData,
            email,
            locationId: storeLocationIdInt,
          });
          history.push(urlPatterns.FORCE_RELOAD, {
            redirectUrl: isPhysicalStoreVisit()
              ? urlPatterns.IN_STORE_QUIZ_RESULTS
              : urlPatterns.QUIZ_RESULTS,
          });
        } else if (props.location.state && props.location.state.redirectUrl) {
          history.push(urlPatterns.FORCE_RELOAD, {
            redirectUrl: getGuestCheckoutLoginRedirect(props.location.state.redirectUrl),
          });

          // If there is a free box campaign discount to be added
        } else if (freeBoxCampaignId) {
          history.push(urlPatterns.FORCE_RELOAD, {
            redirectUrl: urlPatterns.QUIZ_RESULTS,
          });
        } else if (redirectUrlFromParams) {
          // Used in mobile app to redirect non-subscribers to the correct page after login.
          history.push(urlPatterns.FORCE_RELOAD, {
            redirectUrl: getGuestCheckoutLoginRedirect(redirectUrlFromParams),
          });
        } else {
          history.push(urlPatterns.FORCE_RELOAD, {
            redirectUrl: getGuestCheckoutLoginRedirect(urlPatterns.MY_DASHBOARD),
          });
        }
      })

      // Catches error from server (if login unsuccessful) and show message in the form
      .catch((errorData) => {
        const { error } = errorData || {};
        error !== ERROR_MESSAGE_INVALID_GRANT && logError(errorData);
        const errorMessage =
          error === ERROR_MESSAGE_INVALID_GRANT
            ? "That's incorrect.\nNo password, no pinot, we're sorry :("
            : error;

        showErrorAlert(errorMessage || "Sorry, something went wrong!", {
          autoClose: 10000,
        });

        // Stores error from server's response in state variables
        this.setState({
          form: {
            ...state.form,
          },
          isSubmitInProgress: false,
          isSubmitQuizInProgress: false,
        });
      });
  };

  render() {
    const { state } = this;
    const { email, password } = state.form;
    const { genericErrorMessage, isSubmitInProgress, isSubmitQuizInProgress } = state;

    let buttonLabel = "Sign In";
    if (isSubmitInProgress) {
      buttonLabel = "Logging you in...";
    }
    if (isSubmitQuizInProgress) {
      buttonLabel = "Getting recommendations...";
    }

    return (
      <form onSubmit={(event) => this.handleSubmit(event)}>
        <StyledInputHolder>
          <InputField
            type="email"
            placeholder="E-mail Address"
            name="email"
            id="email"
            value={email}
            onChange={this.handleChange}
          />
        </StyledInputHolder>

        <CheckoutInputHolderPassword>
          <InputField
            placeholder="Password"
            name="password"
            id="password"
            type="password"
            value={password}
            onChange={this.handleChange}
          />
          <CheckoutInputHolderPasswordLink to={urlPatterns.PASSWORD_RESET} target="_blank">
            Forgot?
          </CheckoutInputHolderPasswordLink>
        </CheckoutInputHolderPassword>

        {genericErrorMessage && <GlobalAlert message={genericErrorMessage} type="error" />}

        <StyledFormBtn full lg type="submit" disabled={isSubmitInProgress} whiteBgOnHover>
          {buttonLabel}
        </StyledFormBtn>
      </form>
    );
  }
}

export default compose(
  withApollo,
  withRouter,
  graphql(GET_GUEST_FREE_BOX_CAMPAIGN_DISCOUNT, {
    name: "guestFreeBoxCampaignDiscountQuery",
    options: { fetchPolicy: FETCH_POLICY_CACHE_ONLY },
  })
)(LogInForm);
